import { AnimatePresence, motion } from "framer-motion";
import { PropsWithChildren, useState } from "react";

import Archive from "@components/carePlan/Archive";
import styles from "@components/carePlan/CarePlan.module.css";
import CarePlanMenu from "@components/carePlan/CarePlanMenu";
import CareTeam from "@components/carePlan/CareTeam";
import Drive from "@components/carePlan/Drive";
import Plan from "@components/carePlan/Plan";
import Profile from "@components/carePlan/Profile";
import Trackers from "@components/carePlan/Trackers";
import Worksheet from "@components/carePlan/Worksheet";
import GoalTemplatesLibrary from "@components/libraries/GoalTemplatesLibrary";
import MobileHeader from "@components/MobileHeader";
import { CarePlanSectionEnum } from "@typing/Enums";
import { GetJourneyWithPlanQuery } from "@typing/Generated";
import { Direction } from "@typing/Models";
import { fadeAnimation, slideHorizontalAnimation } from "@utils/animationUtils";
import { isMobile } from "@utils/platformUtils";

type Journey = GetJourneyWithPlanQuery["journey"];

type Props = {
  currentSection: CarePlanSectionEnum | null;
  journey: Journey;
  setCurrentSection: (section: CarePlanSectionEnum) => void;
  setWorksheetId: (worksheetId: string | undefined) => void;
  worksheetId: string | undefined;
};

type SectionProps = PropsWithChildren<{
  handleBack: (section: CarePlanSectionEnum) => void;
  section: CarePlanSectionEnum;
}>;

const Section = ({ children, handleBack, section }: SectionProps) => {
  const queryParams = new URLSearchParams(location.search);
  const hideMenu = queryParams.has("hideHeader");

  return (
    <div>
      <div className={styles.relativeContainer}>
        {section !== CarePlanSectionEnum.MENU && !hideMenu && (
          <MobileHeader handleBack={() => handleBack(section)} titleKey={`pages.carePlan.sections.${section}.title`} />
        )}
      </div>

      <div>{children}</div>
    </div>
  );
};

const CarePlan = ({ currentSection, journey, setCurrentSection, setWorksheetId, worksheetId }: Props) => {
  const [swipeDirection, setSwipeDirection] = useState<Direction>("left");

  const handleCurrentSectionChange = (newSection: CarePlanSectionEnum, direction: Direction, worksheetId?: string) => {
    setWorksheetId(worksheetId);
    setSwipeDirection(direction);

    setTimeout(() => setCurrentSection(newSection), 100);
  };

  const animationVariants = isMobile() ? slideHorizontalAnimation(swipeDirection) : fadeAnimation;

  const handleBack = (section: CarePlanSectionEnum) => {
    let newSection = CarePlanSectionEnum.MENU;

    if (section === CarePlanSectionEnum.GOAL_LIBRARY) {
      newSection = CarePlanSectionEnum.CARE_PLAN;
    } else if (section === CarePlanSectionEnum.ARCHIVE) {
      newSection = CarePlanSectionEnum.CARE_PLAN;
    }

    handleCurrentSectionChange(newSection, "left");
  };

  return (
    <div>
      <CarePlanMenu
        currentSection={currentSection}
        handleCurrentSectionChange={handleCurrentSectionChange}
        journey={journey}
        version="desktop"
      />
      <div className={currentSection ? styles.relativeContainer : "ion-hide-lg-down"}>
        <AnimatePresence>
          {(() => {
            switch (currentSection) {
              case CarePlanSectionEnum.ARCHIVE:
                return (
                  <motion.div
                    animate="animate"
                    exit="exit"
                    initial="initial"
                    key={CarePlanSectionEnum.ARCHIVE}
                    style={{ position: "absolute", width: "100%" }}
                    variants={animationVariants}
                  >
                    <Section handleBack={handleBack} section={CarePlanSectionEnum.ARCHIVE}>
                      <Archive
                        completed={false}
                        handleCurrentSectionChange={handleCurrentSectionChange}
                        journey={journey}
                      />
                    </Section>
                  </motion.div>
                );
              case CarePlanSectionEnum.CARE_PLAN:
                return (
                  <motion.div
                    animate="animate"
                    exit="exit"
                    initial="initial"
                    key={CarePlanSectionEnum.CARE_PLAN}
                    style={{ position: "absolute", width: "100%" }}
                    variants={animationVariants}
                  >
                    <Section handleBack={handleBack} section={CarePlanSectionEnum.CARE_PLAN}>
                      <Plan handleCurrentSectionChange={handleCurrentSectionChange} journey={journey} />
                    </Section>
                  </motion.div>
                );
              case CarePlanSectionEnum.CARE_TEAM:
                return (
                  <motion.div
                    animate="animate"
                    exit="exit"
                    initial="initial"
                    key={CarePlanSectionEnum.CARE_TEAM}
                    style={{ position: "absolute", width: "100%" }}
                    variants={animationVariants}
                  >
                    <Section handleBack={handleBack} section={CarePlanSectionEnum.CARE_TEAM}>
                      <CareTeam journey={journey} />
                    </Section>
                  </motion.div>
                );
              case CarePlanSectionEnum.DRIVE:
                return (
                  <motion.div
                    animate="animate"
                    exit="exit"
                    initial="initial"
                    key={CarePlanSectionEnum.DRIVE}
                    style={{ position: "absolute", width: "100%" }}
                    variants={animationVariants}
                  >
                    <Section handleBack={handleBack} section={CarePlanSectionEnum.DRIVE}>
                      <Drive journey={journey} />
                    </Section>
                  </motion.div>
                );
              case CarePlanSectionEnum.GOAL_LIBRARY:
                return (
                  <motion.div
                    animate="animate"
                    exit="exit"
                    initial="initial"
                    key={CarePlanSectionEnum.GOAL_LIBRARY}
                    style={{ position: "absolute", width: "100%" }}
                    variants={animationVariants}
                  >
                    <Section handleBack={handleBack} section={CarePlanSectionEnum.GOAL_LIBRARY}>
                      <GoalTemplatesLibrary
                        handleCurrentSectionChange={handleCurrentSectionChange}
                        journeyId={journey.id}
                      />
                    </Section>
                  </motion.div>
                );
              case CarePlanSectionEnum.MENU:
                return (
                  <motion.div
                    animate="animate"
                    className="ion-hide-lg-up"
                    exit="exit"
                    initial={false}
                    key={CarePlanSectionEnum.MENU}
                    style={{ position: "absolute", width: "100%" }}
                    variants={animationVariants}
                  >
                    <Section handleBack={handleBack} section={CarePlanSectionEnum.MENU}>
                      <div className={styles.mobileSpacing}>
                        <CarePlanMenu
                          currentSection={currentSection}
                          handleCurrentSectionChange={handleCurrentSectionChange}
                          journey={journey}
                          version="mobile"
                        />
                      </div>
                    </Section>
                  </motion.div>
                );
              case CarePlanSectionEnum.PROFILE:
                return (
                  <motion.div
                    animate="animate"
                    exit="exit"
                    initial="initial"
                    key={CarePlanSectionEnum.PROFILE}
                    style={{ position: "absolute", width: "100%" }}
                    variants={animationVariants}
                  >
                    <Section handleBack={handleBack} section={CarePlanSectionEnum.PROFILE}>
                      <Profile handleCurrentSectionChange={handleCurrentSectionChange} journey={journey} />
                    </Section>
                  </motion.div>
                );
              case CarePlanSectionEnum.TRACKERS:
                return (
                  <motion.div
                    animate="animate"
                    exit="exit"
                    initial="initial"
                    key={CarePlanSectionEnum.TRACKERS}
                    style={{ position: "absolute", width: "100%" }}
                    variants={animationVariants}
                  >
                    <Section handleBack={handleBack} section={CarePlanSectionEnum.TRACKERS}>
                      <Trackers journey={journey} mobileSpacing />
                    </Section>
                  </motion.div>
                );
              case CarePlanSectionEnum.WORKSHEET:
                return (
                  <motion.div
                    animate="animate"
                    exit="exit"
                    initial="initial"
                    key={CarePlanSectionEnum.WORKSHEET}
                    style={{ position: "absolute", width: "100%" }}
                    variants={animationVariants}
                  >
                    <Section handleBack={handleBack} section={CarePlanSectionEnum.WORKSHEET}>
                      {worksheetId && <Worksheet journey={journey} worksheetId={worksheetId} />}
                    </Section>
                  </motion.div>
                );
            }
          })()}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default CarePlan;
